import { theme } from '@Guidelines/Theme/theme';
import SectionLabel from '@UI/Pages/Administration/Sso/SsoFormContainer/Shared/SectionLabel';
import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import { css } from '@emotion/react';
import * as React from 'react';
import { SelectButton, Skeleton } from '@verclam/ui-kit';

const groupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const modeNoteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

/**
 * Reads the *form's* current choice, not the persisted configuration — it must update the instant an
 * administrator touches the mode or the activation switch, before any save. `isEnabled === false`
 * takes priority over the mode: an inactive configuration is not proposed regardless of which mode it
 * is set to. A freshly opened creation form (`isEnabled` still false) always falls into that first case.
 */
function ssoModeNote(mode: string, isEnabled: boolean): string | null {
	if (!isEnabled) {
		return 'Le fournisseur d’identité n’est pas proposé sur l’écran de connexion.';
	}

	if (mode === 'exclusive') {
		return 'L’écran de connexion ne propose plus que le fournisseur d’identité.';
	}

	if (mode === 'coexistence') {
		return 'L’écran de connexion propose les deux chemins.';
	}

	return null;
}

type Props = {
	readonly ssoModes: readonly LabeledValueView[] | null;
	readonly mode: string;
	readonly isEnabled: boolean;
	readonly onChange: (value: string) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

/** The mode choice and the note describing what it changes on the sign-in screen, kept together
 * because the note is only ever meaningful next to the field it describes. */
const AuthenticationModeField: React.FC<Props> = ({
	ssoModes,
	mode,
	isEnabled,
	onChange,
	errorMessage,
	isFormFieldValid,
}) => {
	// `SelectButton` is generic over its value type (defaulting to `string`) and its `onChange` contract
	// always carries `TValue | readonly TValue[] | null` to cover the `multiple` case — this field never
	// sets `multiple`, so a non-string value never actually reaches this handler in practice.
	const handleChange = React.useCallback(
		(value: string | readonly string[] | null) => {
			onChange(typeof value === 'string' ? value : '');
		},
		[onChange],
	);

	const currentModeNote = React.useMemo(() => ssoModeNote(mode, isEnabled), [mode, isEnabled]);

	return (
		<div css={groupStyle}>
			<SectionLabel>MODE</SectionLabel>
			{ssoModes === null ? (
				<Skeleton height={theme.controls.height} />
			) : (
				<SelectButton
					name="mode"
					label="Mode d’authentification"
					isLabelHidden
					required
					allowEmpty={false}
					options={ssoModes}
					optionLabel="label"
					optionValue="value"
					value={mode === '' ? null : mode}
					onChange={handleChange}
					errorMessage={errorMessage}
					isFormFieldValid={isFormFieldValid}
				/>
			)}
			{currentModeNote !== null ? <p css={modeNoteStyle}>{currentModeNote}</p> : null}
		</div>
	);
};

export default AuthenticationModeField;
